import { applyPageShellBg, resolveShellBgFromPathname } from "./pageShellBg";

/** 追加到 H5 URL，供 H5 识别小程序 web-view 环境 */
export const MINIAPP_QUERY_FLAG = "lfMiniapp";
/** 抖音小程序壳追加，供 H5 区分微信/抖音（UA 偶发不准时兜底） */
export const DOUYIN_MINIAPP_QUERY_FLAG = "lfMp";
export const DOUYIN_MINIAPP_QUERY_VALUE = "douyin";
export const MINIAPP_DEFAULT_NAV_TITLE = "龙粉社";
const MINIAPP_WEB_PAGE_PATH = "/pages/web/index";

/** 抖音/字节小程序 web-view JSSDK */
const DOUYIN_JSSDK_SRC =
  "https://lf1-cdn-tos.bytegoofy.com/goofy/developer/jssdk/jssdk-1.2.0.js";

export function isDouyinMiniProgramEnv(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get(DOUYIN_MINIAPP_QUERY_FLAG) === DOUYIN_MINIAPP_QUERY_VALUE) {
    return true;
  }
  const ua = (navigator.userAgent || "").toLowerCase();
  if (ua.includes("toutiaomicroapp")) {
    return true;
  }
  const w = window as Window & { tt?: { miniProgram?: unknown } };
  return !!w.tt?.miniProgram;
}

export function isLfMiniProgramEnv(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get(MINIAPP_QUERY_FLAG) === "1") {
    return true;
  }
  if (isDouyinMiniProgramEnv()) {
    return true;
  }
  const wxEnv = (window as Window & { __wxjs_environment?: string }).__wxjs_environment;
  if (wxEnv === "miniprogram") {
    return true;
  }
  return /miniProgram/i.test(navigator.userAgent || "");
}

/** 默认原生标题栏内容区高度（不含状态栏） */
const DEFAULT_NAV_BAR_CONTENT_PX = 44;

/**
 * 小程序原生顶栏（状态栏 + 导航栏）占物理屏高度。
 * 非小程序返回 0。
 * 微信/抖音当前 webview 均在原生栏下，勿再按此值补 padding（见 viewportFill skip）。
 */
export function getMiniProgramNativeNavChromePx(): number {
  if (typeof window === "undefined" || !isLfMiniProgramEnv()) {
    return 0;
  }
  const w = window as Window & {
    tt?: { getSystemInfoSync?: () => Record<string, unknown> };
    wx?: { getSystemInfoSync?: () => Record<string, unknown> };
  };
  try {
    const sys = w.tt?.getSystemInfoSync?.() ?? w.wx?.getSystemInfoSync?.();
    if (!sys) {
      return DEFAULT_NAV_BAR_CONTENT_PX + 44;
    }
    const statusBar = Number(sys.statusBarHeight) || 0;
    const combined =
      Number(sys.navBarHeight) ||
      Number(sys.navigationHeight) ||
      Number(sys.navigationBarHeight) ||
      0;
    if (combined > statusBar) {
      return combined;
    }
    const titleBar =
      Number(sys.titleBarHeight) ||
      Number(sys.navigationBarHeight) ||
      DEFAULT_NAV_BAR_CONTENT_PX;
    return statusBar + titleBar;
  } catch {
    return DEFAULT_NAV_BAR_CONTENT_PX + 44;
  }
}

/** 从 H5 path（可含 query）提取 pathname */
export function extractPathnameFromH5Path(h5Path: string): string {
  const qIdx = h5Path.indexOf("?");
  return qIdx >= 0 ? h5Path.slice(0, qIdx) : h5Path;
}

/**
 * 小程序 bindload 等场景：是否为平台/空间首页（需同步壳页顶栏色）。
 */
export function isMiniProgramShellHomePath(h5Path: string): boolean {
  const pathname = extractPathnameFromH5Path(h5Path);
  if (!pathname || pathname === "/") {
    return true;
  }
  return /^\/space\/\d{8}\/home\/?$/.test(pathname);
}

/** 是否空间页路径 */
export function isSpacePath(h5Path: string): boolean {
  return /^\/space\/\d{8}\//.test(extractPathnameFromH5Path(h5Path));
}

/** 是否空间首页路径 */
export function isSpaceHomePath(h5Path: string): boolean {
  return /^\/space\/\d{8}\/home\/?$/.test(extractPathnameFromH5Path(h5Path));
}

/** 是否平台首页路径 */
export function isPlatformHomePath(h5Path: string): boolean {
  const pathname = extractPathnameFromH5Path(h5Path);
  return !pathname || pathname === "/";
}

/**
 * 需 redirectTo 重载 web-view 的场景：仅平台首页 ↔ 非平台（进/出空间）。
 * 空间内 Tab / 二级页一律 SPA，禁止因顶栏颜色差异整页刷新。
 */
export function shouldMiniProgramReloadWebView(targetPath: string): boolean {
  if (!isLfMiniProgramEnv()) {
    return false;
  }
  const from = window.location.pathname;
  const to = extractPathnameFromH5Path(targetPath);
  if (!to || from === to) {
    return false;
  }
  return isPlatformHomePath(from) !== isPlatformHomePath(to);
}

type MiniProgramBridge = {
  miniProgram?: {
    navigateTo?: (options: {
      url: string;
      fail?: (err: unknown) => void;
    }) => void;
    redirectTo?: (options: {
      url: string;
      fail?: (err: unknown) => void;
    }) => void;
    reLaunch?: (options: {
      url: string;
      fail?: (err: unknown) => void;
    }) => void;
  };
};

function loadScriptOnce(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`load script failed: ${src}`));
    document.head.appendChild(script);
  });
}

export function loadWxBridge(): Promise<MiniProgramBridge | undefined> {
  const w = window as Window & { wx?: MiniProgramBridge };
  if (w.wx?.miniProgram) {
    return Promise.resolve(w.wx);
  }
  return loadScriptOnce("https://res.wx.qq.com/open/js/jweixin-1.6.0.js")
    .then(() => (window as Window & { wx?: MiniProgramBridge }).wx)
    .catch(() => undefined);
}

export function loadTtBridge(): Promise<MiniProgramBridge | undefined> {
  const w = window as Window & { tt?: MiniProgramBridge };
  if (w.tt?.miniProgram) {
    return Promise.resolve(w.tt);
  }
  return loadScriptOnce(DOUYIN_JSSDK_SRC)
    .then(() => (window as Window & { tt?: MiniProgramBridge }).tt)
    .catch(() => undefined);
}

/** 按宿主加载微信 / 抖音 web-view JSSDK */
export function loadMiniProgramBridge(): Promise<MiniProgramBridge | undefined> {
  if (isDouyinMiniProgramEnv()) {
    return loadTtBridge();
  }
  return loadWxBridge();
}

function buildMiniProgramWebUrl(h5Path: string): string {
  const normalized = h5Path.startsWith("/") ? h5Path : `/${h5Path}`;
  return `${MINIAPP_WEB_PAGE_PATH}?path=${encodeURIComponent(normalized)}`;
}

/** 小程序内跳转：redirectTo 到 web 页，顶栏颜色由小程序 onLoad(path) 解析 */
export function miniProgramNavigate(path: string): void {
  void loadMiniProgramBridge().then((bridge) => {
    const mpUrl = buildMiniProgramWebUrl(path);
    const go = bridge?.miniProgram?.redirectTo ?? bridge?.miniProgram?.reLaunch;
    if (!go || !bridge?.miniProgram) {
      console.warn("[lf-h5][miniapp-nav] miniProgram.redirectTo unavailable");
      return;
    }
    go.call(bridge.miniProgram, {
      url: mpUrl,
      fail: (err) => console.error("[lf-h5][miniapp-nav] redirectTo fail", mpUrl, err),
    });
  });
}

/**
 * SPA 跳转时保留 lfMiniapp / version，避免进子页后环境标记丢失。
 * 微信 web-view 内靠该标记识别小程序环境。
 */
function appendMiniappQuery(path: string): string {
  if (typeof window === "undefined" || !isLfMiniProgramEnv()) {
    return path;
  }
  const qIdx = path.indexOf("?");
  const pathname = qIdx >= 0 ? path.slice(0, qIdx) : path;
  const next = new URLSearchParams(qIdx >= 0 ? path.slice(qIdx + 1) : "");
  const cur = new URLSearchParams(window.location.search);
  if (!next.has(MINIAPP_QUERY_FLAG)) {
    next.set(MINIAPP_QUERY_FLAG, cur.get(MINIAPP_QUERY_FLAG) || "1");
  }
  if (!next.has(DOUYIN_MINIAPP_QUERY_FLAG) && cur.get(DOUYIN_MINIAPP_QUERY_FLAG)) {
    next.set(DOUYIN_MINIAPP_QUERY_FLAG, cur.get(DOUYIN_MINIAPP_QUERY_FLAG)!);
  }
  if (!next.has("version") && cur.get("version")) {
    next.set("version", cur.get("version")!);
  }
  const q = next.toString();
  return q ? `${pathname}?${q}` : pathname;
}

type H5NavigateOptions = { replace?: boolean; state?: unknown };

/**
 * 小程序内：仅平台首页 ↔ 进/出空间 走 redirectTo；其余走 SPA。
 * SPA 默认 push（与浏览器一致），让 web-view 有 history：
 * 系统左缘右滑会先 pop H5，而不是直接关闭小程序。
 * 返回上一页请传 replace: true（或 navigate(-1)）；Tab 切换等仍应显式 replace。
 * 微信无法禁用右滑关闭（disableSwipeBack 自 7.0.5 起失效）；H5 自定义手势也无法拦截系统手势，
 * 故不另做左缘 hook，避免与系统右滑叠加重退。
 */
export function h5NavigateTo(
  navigate: (path: string, options?: H5NavigateOptions) => void,
  path: string,
  options?: H5NavigateOptions
): void {
  if (shouldMiniProgramReloadWebView(path)) {
    miniProgramNavigate(path);
    return;
  }
  const next = appendMiniappQuery(path);
  navigate(next, options);
}

/** 小程序 web-view：标记环境 + 本地 body 背景 */
export function applyMiniProgramPageTheme(pathname: string): void {
  if (!isLfMiniProgramEnv()) {
    return;
  }
  document.documentElement.classList.add("lf-h5-in-miniapp");
  applyPageShellBg(resolveShellBgFromPathname(pathname));
}
